import { i18n as linguiI18n } from '@lingui/core';
import {
  PlatformLanguageCode,
  platformDefaultLanguageCode,
  supportedPlatformLanguageCodes,
  type SupportedPlatformLanguageCode,
} from '@tov-plus/domain';

import adminWebsiteEnMessages from './admin-website/en.json';
import adminWebsiteHeMessages from './admin-website/he.json';
import commonEnMessages from './common/en.json';
import commonHeMessages from './common/he.json';
import communicationsServiceEnMessages from './communications-service/en.json';
import communicationsServiceHeMessages from './communications-service/he.json';
import dashboardWebsiteEnMessages from './dashboard-website/en.json';
import dashboardWebsiteHeMessages from './dashboard-website/he.json';
import documentationWebsiteEnMessages from './documentation-website/en.json';
import documentationWebsiteHeMessages from './documentation-website/he.json';
import guestsWebsiteEnMessages from './guests-website/en.json';
import guestsWebsiteHeMessages from './guests-website/he.json';
import identityServiceEnMessages from './identity-service/en.json';
import identityServiceHeMessages from './identity-service/he.json';
import landingWebsiteEnMessages from './landing-website/en.json';
import landingWebsiteHeMessages from './landing-website/he.json';
import vendorsWebsiteEnMessages from './vendors-website/en.json';
import vendorsWebsiteHeMessages from './vendors-website/he.json';

export const SUPPORTED_LOCALES = supportedPlatformLanguageCodes;
export type SupportedLocale = SupportedPlatformLanguageCode;
export const LOCALIZATION_COMPONENTS = [
  'common',
  'dashboard-website',
  'guests-website',
  'admin-website',
  'vendors-website',
  'landing-website',
  'documentation-website',
  'communications-service',
  'identity-service',
] as const;
export type LocalizationComponent = (typeof LOCALIZATION_COMPONENTS)[number];
export interface MissingTranslationDetails {
  id: string;
  locale: SupportedLocale;
}
export type MissingTranslationReporter = (
  details: MissingTranslationDetails,
) => void;

const DEFAULT_LOCALE: SupportedLocale = platformDefaultLanguageCode;
const DEFAULT_COMPONENT: LocalizationComponent = 'common';
const STORAGE_KEY = 'tov_plus_locale';
const QUERY_KEY = 'locale';
let missingTranslationReporter: MissingTranslationReporter | null = null;
let activeComponent: LocalizationComponent = DEFAULT_COMPONENT;

type MessageCatalog = Record<string, string>;
type ComponentCatalogs = Record<SupportedLocale, MessageCatalog>;

const componentCatalogs: Record<LocalizationComponent, ComponentCatalogs> = {
  common: {
    [PlatformLanguageCode.English]: commonEnMessages,
    [PlatformLanguageCode.Hebrew]: commonHeMessages,
  },
  'dashboard-website': {
    [PlatformLanguageCode.English]: dashboardWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: dashboardWebsiteHeMessages,
  },
  'guests-website': {
    [PlatformLanguageCode.English]: guestsWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: guestsWebsiteHeMessages,
  },
  'admin-website': {
    [PlatformLanguageCode.English]: adminWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: adminWebsiteHeMessages,
  },
  'vendors-website': {
    [PlatformLanguageCode.English]: vendorsWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: vendorsWebsiteHeMessages,
  },
  'landing-website': {
    [PlatformLanguageCode.English]: landingWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: landingWebsiteHeMessages,
  },
  'documentation-website': {
    [PlatformLanguageCode.English]: documentationWebsiteEnMessages,
    [PlatformLanguageCode.Hebrew]: documentationWebsiteHeMessages,
  },
  'communications-service': {
    [PlatformLanguageCode.English]: communicationsServiceEnMessages,
    [PlatformLanguageCode.Hebrew]: communicationsServiceHeMessages,
  },
  'identity-service': {
    [PlatformLanguageCode.English]: identityServiceEnMessages,
    [PlatformLanguageCode.Hebrew]: identityServiceHeMessages,
  },
};

function createActiveCatalogs(component: LocalizationComponent) {
  const componentMessages = componentCatalogs[component];

  return {
    [PlatformLanguageCode.English]: {
      ...componentCatalogs.common[PlatformLanguageCode.English],
      ...componentMessages[PlatformLanguageCode.English],
    },
    [PlatformLanguageCode.Hebrew]: {
      ...componentCatalogs.common[PlatformLanguageCode.Hebrew],
      ...componentMessages[PlatformLanguageCode.Hebrew],
    },
  };
}

let activeCatalogs: ComponentCatalogs = createActiveCatalogs(activeComponent);

function normalizeLocale(input: string | null | undefined): SupportedLocale {
  if (!input) {
    return DEFAULT_LOCALE;
  }

  const lower = input.toLowerCase();
  if (lower.startsWith(PlatformLanguageCode.Hebrew)) {
    return PlatformLanguageCode.Hebrew;
  }

  return platformDefaultLanguageCode;
}

export function resolveInitialLocale() {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const queryLocale = new URL(window.location.href).searchParams.get(QUERY_KEY);
  if (queryLocale) {
    const normalized = normalizeLocale(queryLocale);
    window.localStorage.setItem(STORAGE_KEY, normalized);
    return normalized;
  }

  const storedLocale = window.localStorage.getItem(STORAGE_KEY);
  if (storedLocale) {
    return normalizeLocale(storedLocale);
  }

  return normalizeLocale(window.navigator.language);
}

let initialized = false;

export function ensureI18nInitialized() {
  if (initialized) {
    return;
  }

  linguiI18n.load({
    [PlatformLanguageCode.English]:
      activeCatalogs[PlatformLanguageCode.English],
    [PlatformLanguageCode.Hebrew]: activeCatalogs[PlatformLanguageCode.Hebrew],
  });
  linguiI18n.activate(resolveInitialLocale());
  initialized = true;
}

export function configureLocalizationComponent(
  component: LocalizationComponent,
) {
  if (activeComponent === component) {
    return;
  }

  activeComponent = component;
  activeCatalogs = createActiveCatalogs(component);

  if (initialized) {
    const locale = getLocale();
    linguiI18n.load({
      [PlatformLanguageCode.English]:
        activeCatalogs[PlatformLanguageCode.English],
      [PlatformLanguageCode.Hebrew]:
        activeCatalogs[PlatformLanguageCode.Hebrew],
    });
    linguiI18n.activate(locale);
  }
}

export function setLocale(locale: SupportedLocale) {
  ensureI18nInitialized();
  linguiI18n.activate(locale);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function getLocale() {
  ensureI18nInitialized();
  return normalizeLocale(linguiI18n.locale);
}

export function getLocaleDirection(
  locale: SupportedLocale = getLocale(),
): 'ltr' | 'rtl' {
  return locale === PlatformLanguageCode.Hebrew ? 'rtl' : 'ltr';
}

function getCatalog(locale: SupportedLocale) {
  return activeCatalogs[locale];
}

function resolveCatalogMessage(id: string, locale: SupportedLocale) {
  const localizedValue = (
    getCatalog(locale) as Record<string, string | undefined>
  )[id];

  if (typeof localizedValue === 'string' && localizedValue.trim().length > 0) {
    return localizedValue;
  }

  const defaultValue = (
    getCatalog(DEFAULT_LOCALE) as Record<string, string | undefined>
  )[id];

  if (typeof defaultValue === 'string' && defaultValue.trim().length > 0) {
    return defaultValue;
  }

  return id;
}

function hasTranslation(id: string, locale: SupportedLocale) {
  const value = (getCatalog(locale) as Record<string, string | undefined>)[id];
  return typeof value === 'string' && value.trim().length > 0;
}

function reportMissingTranslation(details: MissingTranslationDetails): void {
  if (!missingTranslationReporter) {
    return;
  }
  missingTranslationReporter(details);
}

export function setMissingTranslationReporter(
  reporter: MissingTranslationReporter | null,
) {
  missingTranslationReporter = reporter;
}

export function msg(id: string) {
  ensureI18nInitialized();
  const locale = getLocale();

  if (!hasTranslation(id, locale)) {
    reportMissingTranslation({
      id,
      locale,
    });
  }

  return resolveCatalogMessage(id, locale);
}

export function msgForLocale(id: string, locale: string | null | undefined) {
  ensureI18nInitialized();
  const normalizedLocale = normalizeLocale(locale);

  if (!hasTranslation(id, normalizedLocale)) {
    reportMissingTranslation({
      id,
      locale: normalizedLocale,
    });
  }

  return resolveCatalogMessage(id, normalizedLocale);
}

export type PluralMessageSet = Partial<Record<Intl.LDMLPluralRule, string>> & {
  other: string;
};

export function selectPluralLabel(
  count: number,
  labels: PluralMessageSet,
): string {
  const locale = getLocale();
  const pluralRule = new Intl.PluralRules(locale).select(Math.abs(count));

  return labels[pluralRule] ?? labels.other;
}

export function formatCountLabel(
  count: number,
  labels: PluralMessageSet,
): string {
  return `${count} ${selectPluralLabel(count, labels)}`;
}

export { linguiI18n };
